import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { catchError, map, Observable, throwError } from "rxjs";
import { ApiResponse } from "../api/api-response.model";

function isApiResponse<T>(value: any): value is ApiResponse<T> {
  return (
    value != null &&
    typeof value === "object" &&
    "errors" in value &&
    Array.isArray(value.errors) &&
    "data" in value
  );
}

function coerceErrors(value: any): string[] {
  if (Array.isArray(value)) return value.map((x) => String(x));
  if (typeof value === "string") return [value];
  return [];
}

export function apiResponseInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  return next(req).pipe(
    map((event) => {
      if (!(event instanceof HttpResponse)) return event;

      const body = event.body as any;
      if (!isApiResponse(body)) return event;

      const errors = coerceErrors(body.errors);
      if (errors.length > 0) return event;

      return event.clone({ body: body.data });
    }),
    catchError((err) => {
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }

      const raw = err.error;

      // New backend format: { data: null, errors: string[], errorCode?: string }
      if (isApiResponse<null>(raw)) {
        const errors = coerceErrors(raw.errors);
        const first = errors[0];
        const errorCode =
          typeof (raw as ApiResponse<null>).errorCode === "string"
            ? (raw as ApiResponse<null>).errorCode
            : undefined;

        // Keep backward compatibility for callsites that read `error.error.error`
        const normalizedPayload = {
          ...raw,
          error: first ?? (raw as any)?.error,
          ...(errorCode ? { errorCode } : {}),
        };

        return throwError(() =>
          new HttpErrorResponse({
            error: normalizedPayload,
            headers: err.headers,
            status: err.status,
            statusText: err.statusText,
            url: err.url ?? undefined,
          })
        );
      }

      // Legacy backend format: { error: string }
      const legacyMessage = (raw as any)?.error;
      if (typeof legacyMessage === "string") {
        const normalizedPayload: ApiResponse<null> & { error: string } = {
          data: null,
          errors: [legacyMessage],
          error: legacyMessage,
          ...((raw as any)?.errorCode
            ? { errorCode: String((raw as any).errorCode) }
            : {}),
        };

        return throwError(() =>
          new HttpErrorResponse({
            error: normalizedPayload,
            headers: err.headers,
            status: err.status,
            statusText: err.statusText,
            url: err.url ?? undefined,
          })
        );
      }

      return throwError(() => err);
    })
  );
}


